import {ColumnGroup} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  CustomEvalTableView,
  StructuredTableColumn,
  StructuredTableRowUpdater,
  GenericStructuredTableColumn,
  StructuredTableDomainGraphType,
  GetCustomEvalArgsOptions,
} from 'notebook/structured-tables/StructuredTableConstants';
import {EphemeralObjectSpec} from 'notebook/structured-tables/util/EphemeralObjectSpec';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {CellIndex, CustomEvalFnAndType, TableColumnType} from 'notebook/tables/TableConstants';
import {StudyableColumnAdapter} from 'studies/studyable/StudiesStructuredTableColumn';
import lazyInit from 'util/lazyInit';

/**
 * Provides implementations for standard behaviors for a particular {@link TableColumnType}.
 */
export interface ColumnAdapter<T extends TableColumnType = TableColumnType> {
  columnType: T;
  isEnabled(): boolean;
  /**
   * Indicate whether objects of the specified `objectType` can have a column of type {@link T}.
   * @param structuredTableInsertionContext This context is currently only passed in by a consumer in specific cases
   * where table functionality depends on it. In all other cases, an empty default is provided.
   * If you need to depend on this context, please verify the DOM tree containing the table has a
   * StructuredTableInsertionContext consumer. See {@link StructuredTableInsertionContext} for more details.
   */
  supports(
    objectType: EphemeralObjectSpec,
    structuredTableInsertionContext: StructuredTableInsertionContextValue
  ): boolean;
  /**
   * Create a column of type {@link T} for the given column group.
   */
  makeColumn(columnGroup: ColumnGroup): StructuredTableColumn;
  /**
   * A {@link StructuredTableRowUpdater} that only updates columns of type {@link T}.
   */
  rowUpdater: StructuredTableRowUpdater;
  /**
   * Post-submission should the column value become uneditable
   */
  doesFinalize: boolean;
  /**
   * Can the user delete the add / remove the column
   */
  isUserConfigurable: boolean;

  /**
   * Returns a CustomEval function or null if there is no custom eval function appropriate for this column.
   * This allows us to inject custom logic into the Excess Evaluator to do things like override the cell's value
   * or add additional validation. See {@link StructuredTableAdapter#getCustomEvalFn} for more details.
   */
  getCustomEvalFn(
    tableView: CustomEvalTableView,
    cellIndex: CellIndex
  ): CustomEvalFnAndType<{tableMetadata: unknown; rowMetadata: unknown; fileId?: string | null}> | null;

  /**
   *  Returns a list of Excess formulas which will get passed along to the customEval function for this type
   *  of table. This might be useful if you want to base one column onto the value of another.
   *  See {@link StructuredTableAdapter#getCustomEvalArgs} for more details.
   */
  getCustomEvalArgs(
    tableView: CustomEvalTableView,
    cellIndex: CellIndex,
    options: GetColumnAdapterCustomEvalArgsOptions
  ): Array<string>;
}

export interface GetColumnAdapterCustomEvalArgsOptions extends GetCustomEvalArgsOptions {
  coreObjectTypes: Array<StructuredTableDomainGraphType>;
}

/**
 * NOTE: Do not directly acccess. Use {@link getAllColumnAdapters} instead.
 *
 * This exists to allow mocking in tests.
 *
 * @package
 */
export const __registry = {
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  adapters: () =>
    ({
      [TableColumnType.STUDIES]: StudyableColumnAdapter,
    }) satisfies {[K in TableColumnType]?: ColumnAdapter<K>},
};

/**
 * Registry of all {@link ColumnAdapter}s.
 *
 * See {@link doesColumnTypeHaveAdapter} for usage recommendations.
 *
 */
export const getAllColumnAdapters =
  // Avoid reading values at module initialization to avoid circular import issues.
  lazyInit(() => __registry.adapters());

/**
 * Check if a {@link TableColumnType} has an associated {@link ColumnAdapter}.
 *
 * This can help with type narrowing when exhausting the {@link TableColumnType} enum:
 * @example
 * function makeColumn(columnType: TableColumnType, columnGroup: ColumnGroup) {
 *    if (doesColumnTypeHaveAdapter(type)) {
 *      return getAllColumnAdapters()[columnType].makeColumn(columnGroup);
 *    }
 *    switch (type) {
 *      case TableColumnType.BASE_COL_TYPE_1:
 *         return ...;
 *      ...
 *      case TableColumnType.BASE_COL_TYPE_10:
 *         return ...;
 *      default:
 *         throw new Error(`Unexpected column type: ${assertNever(type)}`);
 *    }
 * }
 */
export function doesColumnTypeHaveAdapter(
  columnType: TableColumnType
): columnType is keyof ReturnType<typeof getAllColumnAdapters> {
  // eslint-disable-next-line benchlint/no-in-operator
  return columnType in getAllColumnAdapters();
}

/**
 * Get the {@link ColumnAdapter} associated with `columnType` or null if no such adapter exists.
 *
 */
export function getColumnAdapter(columnType: TableColumnType): ColumnAdapter | null {
  return doesColumnTypeHaveAdapter(columnType) ? getAllColumnAdapters()[columnType] : null;
}

/**
 * Returns all column adapters that are currently enabled and match the columnType of one of the given columns.
 * If `columns` is not specified, returns all enabled column adapters.
 */
export function getEnabledColumnAdapters<TIds>(
  columns?: Array<GenericStructuredTableColumn<TIds>>
): Array<ColumnAdapter> {
  const enabledAdapters = Object.values(getAllColumnAdapters()).filter((adapter) => adapter.isEnabled());
  if (columns) {
    const columnTypes = columns.map((column) => column.columnType);
    return enabledAdapters.filter((adapter) => columnTypes.includes(adapter.columnType));
  } else {
    return enabledAdapters;
  }
}

/**
 * For the given object type and column group, construct all StructuredTableColumns associated with the
 * registered column types (see {@link getAllColumnAdapters}}).
 * @param structuredTableInsertionContext This context is currently only passed in by a consumer in specific cases
 * where table functionality depends on it. In all other cases, an empty default is provided.
 * If you need to depend on this context, please verify the DOM tree containing the table has a
 * StructuredTableInsertionContext consumer. See {@link StructuredTableInsertionContext} for more details.
 */
export function toStructuredTableColumnsForObjectType(
  objectSpec: EphemeralObjectSpec,
  columnGroup: ColumnGroup,
  structuredTableInsertionContext: StructuredTableInsertionContextValue
): Array<StructuredTableColumn> {
  return getEnabledColumnAdapters().flatMap((adapter) =>
    adapter.supports(objectSpec, structuredTableInsertionContext) ? adapter.makeColumn(columnGroup) : []
  );
}
